/**
 * shared/types/dates.ts — Date planner: ideas and answers, Places meter, prefs and consent, calendar outbox, idea scan, and the DatesAPI slice.
 *
 * Moved verbatim out of the former shared/types.ts monolith; the public
 * surface is unchanged and shared/types/index.ts re-exports it.
 */
import type { GeoPoint, PlaceCandidate, PlaceSkipReason, PlaceSearchQuery } from '../date-places.ts';
import type { DateNudgeResult } from '../date-nudge.ts';

/* ---- 4b. Date Planner (card ZdYBVP203b8a, PRD v1 §5, §11 Phase A) ---- */

/** Lifecycle as PRD §5.2 stores it. The two AFFIRMED_BY_* steps are derived
 *  from date_answers rather than written as rows: an answer pair is the
 *  only thing that ever produces them, and deriving keeps state and answers
 *  from drifting apart. */
export const DATE_STATES = ['pool', 'suggested', 'locked', 'completed', 'raincheck'] as const;
export type DateState = (typeof DATE_STATES)[number];

export const DATE_SOURCES = [
  'agent', 'nomination_a', 'nomination_b', 'quick_add_a', 'quick_add_b', 'template',
] as const;
export type DateSource = (typeof DATE_SOURCES)[number];

/** Phase A has no Places API (PRD §11), so GO_OUT ideas only arrive from the
 *  couple's own capture — mode is still a first-class column for Phase B. */
export const DATE_MODES = ['stay_in', 'go_out', 'virtual'] as const;
export type DateMode = (typeof DATE_MODES)[number];

export const DATE_COST_BANDS = ['free', '$', '$$', '$$$'] as const;
export type DateCostBand = (typeof DATE_COST_BANDS)[number];

export const DATE_ENERGY = ['low', 'med', 'high'] as const;
export type DateEnergy = (typeof DATE_ENERGY)[number];

/** PRD §6.2a chips — one tap, never free text. 'other' carries the text. */
export const REVISE_REASONS = ['too_expensive', 'too_far', 'not_feeling_it', 'bad_timing', 'other'] as const;
export type ReviseReason = (typeof REVISE_REASONS)[number];

export const DATE_ANSWERS = ['affirm', 'revise'] as const;
export type DateAnswer = (typeof DATE_ANSWERS)[number];

/** PRD §11: calendar write on mutual affirm. 'not_connected' is a real,
 *  user-facing outcome (no Google grant yet), not an error. 'needs_time' is
 *  the other honest one: the couple is connected but the date has no `when`
 *  yet, so there is nothing a calendar could be told. */
export type DateCalendarStatus = 'none' | 'queued' | 'not_connected' | 'needs_time' | 'written' | 'failed';

export interface DateIdea {
  ideaId: string;
  title: string;
  note: string | null;
  source: DateSource;
  mode: DateMode;
  costBand: DateCostBand;
  energy: DateEnergy;
  placeName: string | null;
  placeUrl: string | null;
  /** PRD §5.1 placeRef — the Places identity behind a GO_OUT idea, plus the
   *  address the card shows. Null for curated templates and anything the
   *  couple typed themselves. Coordinates are deliberately NOT on the view:
   *  they exist for scoring only and never leave the box (pinned by
   *  scripts/probe-dates-prefs p12). */
  placeId: string | null;
  placeAddress: string | null;
  /** PRD §7 step 4 — why this card, in the concierge's voice. Templates keep
   *  their curated `note`; a Places card ships this instead. */
  explainer: string | null;
  /** Auto line for quick-adds ("from Maya's notebook, 12 Mar"). */
  provenance: string | null;
  state: DateState;
  templateKey: string | null;
  suggestedAt: number | null;
  lockedAt: number | null;
  completedAt: number | null;
  /** The date's actual start time (ms epoch), set at capture or later from
   *  the LOCKED card. null = no time yet, which is why the calendar write
   *  cannot happen on lock alone. */
  when: number | null;
  calendarStatus: DateCalendarStatus;
  calendarEventId: string | null;
  /** Last revise reason this idea drew — drives the next explainer line. */
  vetoReason: ReviseReason | null;
  rounds: number;
  createdBy: string | null;
  createdAt: number;
  updatedAt: number;
}

/**
 * What a partner is allowed to see (PRD §5.2 invariant: reactions are never
 * revealed to the other partner until LOCKED).
 *
 * `answers` is empty while the round is open — not "empty for the partner",
 * empty for everyone: the only thing a viewer ever gets pre-reveal is their
 * own answer back through `myAnswer`. Nothing here says whether the other
 * person has answered at all.
 */
export interface DateView extends DateIdea {
  myAnswer: DateAnswer | null;
  myReason: ReviseReason | null;
  revealed: boolean;
  /** Present only once `revealed` — { memberId, answer, reason } per member. */
  answers: { memberId: string; answer: DateAnswer; reason: ReviseReason | null }[];
  /** PRD §6.2a: the visible "we heard you" line, post-reveal only. */
  learnedNote: string | null;
}

export interface DateSuggestionResult {
  idea: DateView | null;
  /** PRD §9 guardrail: veto rate over half across 5+ rounds halts the agent. */
  halted: boolean;
  haltedReason: string | null;
  learnedNote: string | null;
  /** Phase B: what the Places run scored, when one happened at all. */
  place: DatePlaceOutcome | null;
}

/** One line of the PRD §7 step 3 ranking — numbers only, no geo, no address. */
export interface DatePlaceScoreLine {
  placesApiId: string;
  score: number;
  fit: number;
  fairness: number;
  novelty: number;
}

/** What the worker's Places search came back with, after the ladder. */
export interface DatePlaceOutcome {
  /** Everything that came back, scored, best first — the ranking the
   *  choice was made from (fairness and novelty asserted from here). */
  ranked: DatePlaceScoreLine[];
  /** Ladder rung that produced candidates: base | widen | relax. */
  step: string | null;
  /** The ONE soft constraint that rung dropped, named for the explainer. */
  relaxed: string | null;
  /** api_key | oauth | none — which credential actually carried the call. */
  credential: string;
  /** HTTP calls made (including credential retries) — the usage meter reads
   *  this, so a bill can never arrive unannounced. */
  attempts: number;
}

/**
 * Phase B: what the worker needs BEFORE it may call Places. Returned by the
 * DO so the network decision — and therefore the spend — happens in one
 * place. `query: null` means "do not call": `skip` says why, in words the
 * probe can assert on.
 */
export interface DateSuggestContext {
  openRound: boolean;
  halted: boolean;
  query: PlaceSearchQuery | null;
  skip: PlaceSkipReason | null;
  /** HTTP attempts already made for this couple today (daily cap input). */
  placesUsedToday: number;
}

/**
 * What the worker hands back with `suggestDateIdea`. Plain JSON on purpose —
 * it crosses the DO's RPC boundary, so nothing here may hold a binding.
 */
export interface DatePlaceSearch {
  candidates: PlaceCandidate[];
  /** Ladder rung that produced them (null = nothing came back). */
  step: string | null;
  relaxed: string | null;
  credential: string;
  attempts: number;
  reason: string | null;
  capped: boolean;
  /** Radius actually used, metres — the scorer measures fairness against it. */
  radiusM: number;
}

/** PRD verify line: "Places usage metered and reported before any bill". */
export interface DatePlacesUsage {
  /** Ladder runs recorded (a run can hold up to 3 HTTP attempts). */
  searches: number;
  httpAttempts: number;
  candidates: number;
  /** Estimate at USD 32/1,000 — a number to report, not a charge. */
  estCostUsd: number;
  usedToday: number;
  dailyMax: number;
  capped: boolean;
  lastAt: number | null;
}

/**
 * Card 2qBAZOM2LFWa — the pre-flight for a card-detail place search.
 * `origins` is null unless that half consented to precise location, exactly as
 * the date planner reads it; a card's own detail never overrides consent.
 */
export interface PlaceSearchContext {
  usedToday: number;
  dailyMax: number;
  origins: { a: GeoPoint | null; b: GeoPoint | null };
}

/* ------------------------------------------------------------------ *
 * PRD §6.4 — Preferences & consent (card sXseXczawWR1)
 * ------------------------------------------------------------------ */

export const DATE_LOCATION_CONSENTS = ['precise', 'city', 'off'] as const;
export type DateLocationConsent = (typeof DATE_LOCATION_CONSENTS)[number];

export const DATE_TIMINGS = ['weekday_eve', 'weekend_day', 'weekend_eve'] as const;
export type DateTiming = (typeof DATE_TIMINGS)[number];

/** A short, closed vocabulary — free text never reaches the agent scorer. */
export const DATE_CONSTRAINTS = ['near_transit', 'kid_friendly', 'vego', 'no_late_nights', 'outdoorsy'] as const;
export type DateConstraint = (typeof DATE_CONSTRAINTS)[number];

/** Per-factor tri-state attitude (card GMat5z0Zxutf). The decision factors
 *  ARE the constraint set — each gets Desirable / Neutral / Undesirable, and
 *  Undesirable is what lands back in `constraints` so the intersection and
 *  the scorer keep working on the exact rule-out list they already read. */
export const DATE_FACTOR_ATTITUDES = ['desirable', 'neutral', 'undesirable'] as const;
export type DateFactorAttitude = (typeof DATE_FACTOR_ATTITUDES)[number];

/** One member's saved row. Coordinates exist ONLY while consent is
 *  'precise'; 'city' keeps a label, 'off' keeps neither. */
export interface DateMemberPrefs {
  memberId: string;
  /** Dates per month the concierge aims for (1–4). */
  cadence: number;
  /** Weekday the suggestion should land by (0=Sun … 6=Sat). */
  suggestByDay: number;
  timing: DateTiming[];
  /** 0 = all stay-in, 100 = all go-out. */
  modeRatio: number;
  budget: DateCostBand;
  energy: DateEnergy;
  constraints: DateConstraint[];
  /** Per-factor tri-state (card GMat5z0Zxutf): Desirable / Neutral /
   *  Undesirable for each decision factor. `constraints` is derived from
   *  the Undesirable set, so the two can never disagree. */
  factorAttitudes: Record<DateConstraint, DateFactorAttitude>;
  locationConsent: DateLocationConsent;
  locationLabel: string | null;
  lat: number | null;
  lng: number | null;
  updatedAt: number;
}

/** prefs(A) ∩ prefs(B) — null-able fields mean the two said different
 *  things, i.e. there is no overlap to score against. */
export interface DatePrefsIntersection {
  cadence: number;
  suggestByDay: number | null;
  timing: DateTiming[];
  modeRatio: number | null;
  budget: DateCostBand;
  energy: DateEnergy | null;
  constraints: DateConstraint[];
  /**
   * Card GMat5z0Zxutf: factors BOTH members marked Desirable. Leaning is not
   * ruling out, so this is the union-free intersection — one partner's
   * preference never outvotes the other (same contract as every other field
   * here). The suggest path reads it to steer WHICH kind of place the
   * concierge looks for when neither member has ruled that factor out.
   */
  desirables: DateConstraint[];
}

export type DateDerivedMode = 'together' | 'long_distance' | 'unspecified';

/** What GET /api/dates/prefs returns. `mine` is the only object that can
 *  carry coordinates; the partner's copy is stripped to the consent level
 *  (PRD §6.4: "Your partner never sees where you are"). */
export interface DatePrefsView {
  mine: DateMemberPrefs;
  partner: DateMemberPrefs | null;
  /** Null until BOTH members have saved a row — one partner's set drives
   *  nothing, by design. */
  intersection: DatePrefsIntersection | null;
  derivedMode: DateDerivedMode;
  /** The banner's "how we know" line, verbatim. */
  derivedModeReason: string;
}

/**
 * One row of the date calendar outbox (the flush's unit of work). The queue
 * holds ids, an op and a counter only — the flush reads the idea row itself
 * for the title/time, so nothing couple-facing is duplicated here.
 */
export interface DateCalendarItem {
  ideaId: string;
  op: 'insert' | 'update';
  attempts: number;
  /** Google's id for this event, null until the first push succeeds. */
  externalId: string | null;
  /** The idea as it stands NOW — not as it stood when queued. */
  title: string;
  when: number;
  placeName: string | null;
  note: string | null;
}

export interface DateCalendarStats {
  /** Queued rows still under the retry ceiling. */
  pending: number;
  /** Rows that hit the ceiling and are parked until a time change re-enqueues them. */
  failed: number;
}

/* ---- 4y1. Date-idea proposals from the concierge (card qyhVbyMpcN0B) ---- */

/**
 * One proposal the scan accepts from the model: a title, the one-line
 * rationale that becomes the card's note, and the three closed-vocabulary
 * fields the scorer and the panel read. Anything else the model sends is
 * dropped at the parse boundary — the same rule the notebook scan applies
 * to themes.
 */
export interface DateIdeaProposal {
  title: string;
  note: string;
  mode: DateMode;
  costBand: DateCostBand;
  energy: DateEnergy;
}

export type DateIdeasScanStop =
  | 'toggle_off'
  | 'no_model_key'
  | 'no_signal'
  | 'pool_full'
  | 'model_error'
  /* Pear Plus (card 32BEnNO6I2kx): the date concierge is a Plus surface, so
     an Essentials couple's daily scan stops here — the same "stop reason is
     the answer" contract as the toggles. */
  | 'plus_required'
  | 'emitted';

export interface DateIdeasScanResult {
  scanned: boolean;
  reason: DateIdeasScanStop;
  /** Ideas persisted by this run (0 when the run stopped early). */
  count: number;
  /** Signal rows the prompt was built from (interests profiles, revealed
   *  game rounds, shared notebook items). */
  considered: number;
  /** Theme-tagged notebook links offered to the model this run (card
   *  TbzvQa3ZgXZO) — 0 whenever the Plus gate is closed or the couple has
   *  no themed link, which is the honest answer, not an error. */
  links: number;
}

/**
 * One shared, theme-tagged notebook LINK handed to the date-idea scan
 * (card TbzvQa3ZgXZO): "It could be surfaced via date recommendations eg
 * stay in and read". `theme` is the EDITORIAL label (notebookThemeLabel) so
 * the model reads human words; the raw key stays in the notebook where
 * search and filters need it. A candidate is an offer, never a claim: it
 * says nothing about whether anyone has read the link.
 */
export interface NotebookLinkCandidate {
  itemId: string;
  title: string;
  theme: string;
  createdAt: number;
}

/** Everything the daily date-idea scan needs to build one prompt. Plain
 *  reads, no serial(): the insert happens later, in its own serial(). */
export interface DateIdeasScanSeed {
  /** Topics per partner, both profiles flattened. */
  interests: string[];
  /** Revealed rounds only — an unrevealed answer never reaches a prompt. */
  game: { question: string; answers: string[] }[];
  /** Shared notebook entries (title + description), newest first. */
  notebook: string[];
  /** Theme-tagged shared notebook LINKS, newest first — the "stay in and
   *  read" candidates (card TbzvQa3ZgXZO). Empty when the Pear Plus gate is
   *  closed or the couple has no themed link; the rest of the seed stands. */
  linkCandidates: NotebookLinkCandidate[];
  /** Titles already in date_ideas, dismissed or not — the dedupe set. */
  knownTitles: string[];
  /** Dismissed titles, sent to the model as the avoid list. */
  dismissed: string[];
  poolCount: number;
  prefs: DatePrefsIntersection | null;
}

/** Lives on the DO itself (it needs env + the alarm), not on the slice —
 *  the same shape as NotebookScanAPI (card qyhVbyMpcN0B). */
export interface DateIdeasScanAPI {
  runDateIdeasScan(): Promise<DateIdeasScanResult>;
}

export interface DatesAPI {
  /** Vault quick-add / nomination → POOL (PRD §6.1). Capture is permissive. */
  captureDateIdea(input: {
    addedBy: string;
    title: string;
    note?: string | null;
    placeName?: string | null;
    placeUrl?: string | null;
    mode?: DateMode | null;
    costBand?: DateCostBand | null;
    energy?: DateEnergy | null;
    source?: DateSource | null;
    when?: number | null;
  }): Promise<DateView>;
  /** Every idea the couple owns, each rendered for `viewerId`. */
  listDateIdeas(viewerId: string): Promise<DateView[]>;
  /** Surface the next SUGGESTED idea: pool first, Phase B Places candidates
   *  second, then the template library. `now` is dev-only, same contract as
   *  tickRequests; `search` is the worker's Places result (see below). */
  /** `generate` (card gMiWQUTVrPSU) asks for a FRESH candidate: the pool pick
   *  is skipped so the ladder goes Places → template. An open round and a
   *  guardrail halt still win — one couple, one open card, one spend meter. */
  suggestDateIdea(
    viewerId: string,
    now?: number,
    search?: DatePlaceSearch | null,
    generate?: boolean,
  ): Promise<DateSuggestionResult>;
  /** Phase B, step 1 of a suggest: everything the worker must know BEFORE
   *  it may spend a call — an open round, a guardrail halt or a pool with
   *  ideas in it all answer `query: null` (with the reason) and no Places
   *  request is made. `generate: true` drops only `pool_has_ideas`, so a
   *  generate may go out and buy the call the saved pool would have blocked. */
  dateSuggestContext(viewerId: string, generate?: boolean): Promise<DateSuggestContext>;
  /** The meter behind "reported before any bill" (PRD §10 / card verify). */
  datePlacesUsage(): Promise<DatePlacesUsage>;
  /** Card 2qBAZOM2LFWa — pre-flight for a card-detail place search: today's
   *  count, the ceiling, and origins only where precise consent was given. */
  placeSearchContext(): Promise<PlaceSearchContext>;
  /** Card 2qBAZOM2LFWa — book one card-detail search against the same meter,
   *  and hand back the refreshed usage so the spend travels with the results. */
  recordPlaceSearch(search: DatePlaceSearch): Promise<DatePlacesUsage>;
  /** Send one pool idea into the loop ("Send to partner", PRD §6.2b). */
  promoteDateIdea(ideaId: string, viewerId: string): Promise<DateView>;
  /** Affirm or Revise. Both affirm → LOCKED + calendar write. Reveals answers. */
  answerDateIdea(input: {
    ideaId: string;
    memberId: string;
    answer: DateAnswer;
    reason?: ReviseReason | null;
    now?: number;
  }): Promise<DateView>;
  /** PRD §6.2c: close a locked date as completed, or raincheck it. */
  setOutcome(input: {
    ideaId: string;
    memberId: string;
    outcome: 'completed' | 'raincheck';
    now?: number;
  }): Promise<DateView>;
  /** PRD §9: a suggestion with 48h of silence dissolves back to POOL and its
   *  answers are dropped so nobody is anchored by a half-answered card. */
  dissolveSilentSuggestions(now: number): Promise<number>;
  /** Counters behind the PRD §9 guardrail, for the UI status line. */
  dateGuardrail(): Promise<{ rounds: number; vetoes: number; halted: boolean; message: string | null }>;
  /** PRD §7 cadence fit / §9 re-time rule (card yejoF8uTVxyf): the due-check
   *  that runs when the Dates surface is READ. Decides (and claims) one
   *  couple-addressed Thread nudge per local month, only inside a
   *  Thursday-evening or Monday slot; `now` and `offsetMinutes` come from the
   *  caller so the slot is the couple's local time and a dev probe can
   *  override the clock. The message itself is posted by the room wrapper,
   *  outside serial(). */
  dateCadenceNudge(input: { now: number; offsetMinutes: number }): Promise<DateNudgeResult>;
  /** Set (or move) the date's start time. On a LOCKED idea this re-queues the
   *  calendar write as op=update — the flush picks it up on the next pass. */
  setDateWhen(input: { ideaId: string; memberId: string; when: number }): Promise<DateView>;
  /** Queued calendar work, oldest first, only while the couple is connected.
   *  A row whose idea has no `when` stays queued — there is nothing to write. */
  dateCalendarBatch(limit?: number): Promise<DateCalendarItem[]>;
  dateCalendarAck(ideaId: string, externalId: string): Promise<void>;
  dateCalendarFail(ideaId: string, error: string): Promise<void>;
  dateCalendarStats(): Promise<DateCalendarStats>;
  /** PRD §6.4: the member's own prefs, the partner's stripped copy, the
   *  intersection (null until both saved) and the derived mode banner. */
  datePrefs(memberId: string): Promise<DatePrefsView>;
  /** Merge a partial update onto the member's row and return the view.
   *  A consent other than 'precise' deletes any stored coordinates. */
  saveDatePrefs(memberId: string, patch: Record<string, unknown>): Promise<DatePrefsView>;
  /** "Not for us" (card qyhVbyMpcN0B): park a POOL idea behind
   *  `dismissed_at` so it leaves every list and becomes part of the scan's
   *  avoid list. POOL only — anything further along the pipeline is a 409,
   *  because a locked date is a decision, not a proposal. */
  dismissDateIdea(ideaId: string, viewerId: string): Promise<DateView>;
  /** The prompt's raw material: interests, revealed game rounds, shared
   *  notebook items, the dedupe/avoid title sets and the prefs intersection.
   *  Plain reads — no serial(), the caller inserts in its own serial(). */
  dateIdeasScanSeed(opts?: { plus?: boolean }): Promise<DateIdeasScanSeed>;
  /** Persist a validated batch: clamped to 3, deduped against every known
   *  title (dismissed included), budget-filtered, state=pool/source=agent. */
  recordDateIdeas(input: { proposals: DateIdeaProposal[]; at: number }): Promise<number>;
}
